"use client";
import { useRef, useState } from "react";
import { ProductDataProps } from "../../../app/utilities/interfaces";
import { processCarFeatures } from "../../../app/utilities/index";

const INITIAL_VISIBLE = 6;

function FeatureItem({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <svg
        className="w-4 h-4 text-green-500 shrink-0"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"
          clipRule="evenodd"
        />
      </svg>
      <span className="text-gray-800">{text}</span>
    </div>
  );
}

export default function Features({ data }: ProductDataProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const model: any = (data as any)?.modelData ?? data;

  const heading = `${model?.brand_name ?? model?.brand?.name ?? ""} ${
    model?.name ?? ""
  } Key Features`.trim();

  const features = processCarFeatures(data).map((f) => f.label);
  const hasMore = features.length > INITIAL_VISIBLE;

  const handleToggle = () => {
    if (isExpanded) {
      setIsExpanded(false);
      const section = sectionRef.current;
      if (!section) return;

      const headerHeight =
        document.querySelector("header")?.getBoundingClientRect().height ?? 73;
      const secondaryNavHeight =
        document.querySelector("[data-secondary-nav]")?.getBoundingClientRect()
          .height ?? 0;
      const top =
        section.getBoundingClientRect().top +
        window.scrollY -
        (headerHeight + secondaryNavHeight) -
        8;

      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      return;
    }
    setIsExpanded(true);
  };

  return (
    <section id="key-features" ref={sectionRef}>
      <div className="mb-3">
        <h2 className="text-[20px] font-bold heading-p leading-[36px]">
          {heading}
        </h2>
      </div>

      <div className="bg-white rounded-lg p-4 md:p-6 border border-gray-200 mb-4 md:mb-6">
        {features.length === 0 ? (
          <div className="text-gray-500">No feature data available.</div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 md:gap-4">
              {features.map((feature, i) => (
                <div
                  key={feature + i}
                  className={!isExpanded && i >= INITIAL_VISIBLE ? "hidden" : ""}
                >
                  <FeatureItem text={feature} />
                </div>
              ))}
            </div>
            {hasMore && (
              <button
                type="button"
                onClick={handleToggle}
                aria-expanded={isExpanded}
                className="mt-3 text-[14px] font-semibold text-teal-700 hover:text-teal-800 cursor-pointer bg-transparent border-none p-0"
              >
                {isExpanded ? "Read Less" : "Read More"}
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}
